import { useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CirclePlusIcon } from 'lucide-react';
import { useFieldArray, useForm, useWatch, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import fs from '@/components/atoms/Fieldset';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { languageAndFlagByCountryCode } from '@/shared/languageCodes';
import { OptionBase } from '@/shared/types/common';
import {
  AccordionFieldsetProps,
  CustomFieldType,
  Field,
  WebWidgetIntegration,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';
import { trimOnBlur } from '@/util/validation/helpers';
import { integrationRulesWebWidget } from '@/util/validation/rules/integration-rules';

import VisitorFormCard from './VisitorFormCard/VisitorFormCard';

import styles from './VisitorInformation.module.scss';

export interface FieldOption {
  value: string;
  label_key: string;
}

interface FormField extends Omit<Field, 'languages'> {
  languages?: OptionBase[];
}

export const fieldOptions: FieldOption[] = [
  { value: 'email', label_key: 'Email' },
  { value: 'display_name', label_key: 'Name' },
  { value: 'phone', label_key: 'Phone' },
  { value: 'address', label_key: 'Address' },
  { value: 'language', label_key: 'Language' },
  { value: 'custom', label_key: 'Custom' },
];

export const requiredOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export type Form = {
  fields: FormField[];
  headline: string;
  disclaimer: string;
  disclaimer_checkbox: boolean;
  collect_data_checkbox: boolean;
  collect_location_checkbox: boolean;
};

const defaultField = {
  field: '',
  required: true,
  languages: [],
  field_label: '',
  variable: '',
  variable_type: '' as CustomFieldType,
};

const VisitorInformation = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const [languageOptions, setLanguageOptions] = useState<OptionBase[]>([]);

  // Parse form fields to the expected format for the integration
  const formToIntegrationFields = (fields: FormField[]): Field[] => {
    if (!fields || fields.length === 0) {
      return [];
    }

    return fields?.map((x: FormField) => {
      const baseField: Field = { field: x.field, required: x.required };
      if (x.field === 'custom') {
        baseField.field_label = x.field_label?.trim() || undefined;
        baseField.variable = x.variable?.trim() || undefined;
        baseField.variable_type = x.variable_type || undefined;
      } else if (x.field === 'language') {
        baseField.languages = x?.languages?.map((item) => item.value) || undefined;
      }
      return baseField;
    });
  };
  const integrationToFormFields = useCallback(
    (fields: Field[] = []): FormField[] => {
      if (fields.length === 0) {
        return [defaultField];
      }

      return fields.map((field) => ({
        field: field.field,
        required: field.required,
        field_label: field.field_label || '',
        variable: field.variable || '',
        variable_type: field.variable_type || ('' as CustomFieldType),
        languages:
          field.field === 'language' && languageOptions
            ? (field.languages || []).map((key) => ({
                label: formatLanguageLabel(key),
                value: key.toString(),
              }))
            : [],
      }));
    },
    [languageOptions]
  );
  const formMethods = useForm<Form>({
    mode: 'onSubmit',
    defaultValues: {
      fields: [defaultField],
      headline: integration?.config?.visitor_information?.headline || '',
      disclaimer: integration?.config?.visitor_information?.disclaimer?.html || '',
      disclaimer_checkbox: !!integration?.config?.visitor_information?.disclaimer?.html,
      collect_data_checkbox:
        !!integration?.config?.visitor_information?.fields ||
        !!integration?.config?.visitor_information?.headline,
      collect_location_checkbox: Boolean(integration?.config?.visitor_information?.location),
    },
    resolver: yupResolver(integrationRulesWebWidget.visitor_information) as Resolver<Form>,
  });

  const {
    reset,
    formState: { isDirty },
    control,
    handleSubmit,
    getValues,
    trigger,
  } = formMethods;

  const [collectDataCheckbox, disclaimerCheckbox] = useWatch({
    control,
    name: ['collect_data_checkbox', 'disclaimer_checkbox'],
  });
  // Only the types: watching whole fields would re-render every card on each keystroke
  const fieldTypes = useWatch({
    control,
    name: 'fields',
    compute: (fields) => fields.map(({ field }) => field),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'fields' });

  useEffect(() => {
    reset({
      fields: integrationToFormFields(integration?.config?.visitor_information?.fields),
      headline: integration?.config?.visitor_information?.headline || '',
      disclaimer: integration?.config?.visitor_information?.disclaimer?.html || '',
      disclaimer_checkbox: !!integration?.config?.visitor_information?.disclaimer?.html,
      collect_data_checkbox:
        !!integration?.config?.visitor_information?.fields ||
        !!integration?.config?.visitor_information?.headline,
      collect_location_checkbox: Boolean(integration?.config?.visitor_information?.location),
    });
  }, [
    integrationToFormFields,
    integration?.config?.visitor_information?.disclaimer?.enabled,
    integration?.config?.visitor_information?.disclaimer?.html,
    integration?.config?.visitor_information?.fields,
    integration?.config?.visitor_information?.headline,
    integration?.config.visitor_information?.location,
    reset,
  ]);

  useEffect(() => {
    const options = prioritizeLanguageOptions(
      Object.keys(languageAndFlagByCountryCode).reduce<OptionBase[]>((acc, key) => {
        if (!languageAndFlagByCountryCode[key]?.display_only) {
          return [...acc, { label: formatLanguageLabel(key), value: key }];
        }
        return acc;
      }, [])
    );
    setLanguageOptions(options);
  }, [reset]);

  const handleAddField = useCallback(() => {
    append(defaultField);
  }, [append]);

  const handleDelete = useCallback(
    (index: number) => () => {
      remove(index);
    },
    [remove]
  );

  const handleUpdateIntegration = useCallback(() => {
    const {
      headline,
      fields,
      disclaimer,
      collect_data_checkbox,
      disclaimer_checkbox,
      collect_location_checkbox,
    } = getValues();
    const trimmedDisclaimer = disclaimer.trim();
    const newFields = formToIntegrationFields(fields);
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          visitor_information:
            collect_data_checkbox || disclaimer_checkbox || collect_location_checkbox
              ? {
                  headline: collect_data_checkbox ? headline || undefined : undefined,
                  fields: collect_data_checkbox ? newFields : undefined,
                  disclaimer:
                    disclaimer_checkbox && trimmedDisclaimer
                      ? { html: trimmedDisclaimer, enabled: disclaimer_checkbox }
                      : undefined,
                  location: collect_location_checkbox,
                }
              : undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [dispatch, expanded, isDirty, reset, type]);

  return (
    <Accordion
      title={t('integrations.web_visitor.title')}
      subtitle={t('integrations.web_visitor.subtitle')}
      footerText={t('integrations.web_visitor.footer')}
      onSubmit={handleSubmit(handleUpdateIntegration)}
      disabled={!isDirty}
      isLoading={updateStatus === 'pending'}
      expanded={expanded === type}
      handleChange={toggleAccordion(type)}
      readOnly={!canWrite}
      ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
    >
      <Form {...formMethods}>
        <div className={styles.formConfigWrapper}>
          <FormField
            control={control}
            name="collect_data_checkbox"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <fs.Checkbox
                    label={
                      <TitleSubtitle
                        title={t('integrations.web_visitor.title2')}
                        subtitle={t('integrations.web_visitor.subtitle2')}
                        noGutters
                      />
                    }
                    onChange={onChange}
                    checked={value}
                    disabled={!canWrite}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {collectDataCheckbox && (
            <>
              <div className={styles.textArea}>
                <FormField
                  control={control}
                  name="headline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="headline" className={styles.textArea__label}>
                        {t('integrations.web_visitor.headline')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value ?? ''}
                          onBlur={trimOnBlur(field)}
                          id="headline"
                          placeholder={t('integrations.web_visitor.placeholder')}
                          readOnly={!canWrite}
                          minRows={3}
                          maxRows={10}
                          className={cn(
                            !canWrite &&
                              'bg-bg-muted-50 text-foreground-muted focus-visible:ring-0 focus-visible:ring-offset-0'
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <span className={styles.textAreaHelp}>{t('integrations.web_visitor.help')}</span>
              {canWrite && (
                <Button
                  onClick={handleAddField}
                  variant="ghost"
                  size="md"
                  className="w-fit [&_svg]:text-foreground-muted"
                >
                  <CirclePlusIcon size={16} />
                  {t('integrations.web_visitor.add_field')}
                </Button>
              )}
              <div className="flex flex-col gap-4">
                {fields.map((field, index) => (
                  <VisitorFormCard
                    key={field.id}
                    index={index}
                    onDelete={handleDelete(index)}
                    control={control}
                    languageOptions={languageOptions}
                    fieldTypes={fieldTypes}
                  />
                ))}
              </div>
            </>
          )}

          <FormField
            control={control}
            name="disclaimer_checkbox"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <fs.Checkbox
                    label={
                      <TitleSubtitle
                        title={t('integrations.web_visitor.disclaimer_title')}
                        subtitle={t('integrations.web_visitor.disclaimer_subtitle')}
                        noGutters
                      />
                    }
                    onChange={onChange}
                    checked={value}
                    disabled={!canWrite}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {disclaimerCheckbox && (
            <>
              <div className={styles.textArea}>
                <FormField
                  control={control}
                  name="disclaimer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="disclaimer" className={styles.textArea__label}>
                        {t('integrations.web_visitor.textarea_label')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          id="disclaimer"
                          placeholder={t('integrations.web_visitor.disclaimer_placeholder')}
                          disabled={!canWrite}
                          minRows={3}
                          maxRows={30}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription className={styles.disclaimerText}>
                        {t('integrations.web_visitor.disclaimer_text')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
          <FormField
            control={control}
            name="collect_location_checkbox"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <fs.Checkbox
                    label={
                      <TitleSubtitle
                        title={t('integrations.web_visitor.collect_location_title')}
                        subtitle={t('integrations.web_visitor.collect_location_subtitle')}
                        noGutters
                      />
                    }
                    onChange={onChange}
                    checked={value}
                    disabled={!canWrite}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </Accordion>
  );
};

export default VisitorInformation;
